import React, { useRef, useState } from 'react';
import classNames from 'classnames';

import { EditableText, selectAllText } from '../EditableText';
import type { EditableTextProps, EditableTextElement } from '../EditableText';

import { NonEditableText } from './components';
import type { PlainTextEditorProps } from './types';
import { truncatePlaceholderText } from './utilities';

import './PlainTextEditor.scss';

export function PlainTextEditor({
    id,
    placeholder,
    value,
    // TODO: Should be renamed `maxLength`.
    characterLimit,
    editMode = false,
    editing = false,
    heading = false,
    onEnableEditing,
    onDisableEditing,
    onChange,
    onUpdateValue,
}: PlainTextEditorProps) {
    const elementRef = useRef<EditableTextElement | null>(null);

    // const [remainingLength, setRemainingLength] = useState(characterLimit);
    // This state is needed because we don't send `value` updates back to the parent
    // until `onUpdateValue()` is called at the end of the editing session.
    const [suppressPlaceholder, setSuppressPlaceholder] = useState(Boolean(value));

    const currentlyEditing = editMode && editing;
    const trimmedPlaceholder = characterLimit ? truncatePlaceholderText(placeholder, characterLimit) : placeholder;

    function handleEnableEditing() {
        if (!editing) {
            onEnableEditing();
        }
    }

    const handleFocus: EditableTextProps['onFocus'] = (editedValue) => {
        setSuppressPlaceholder(Boolean(editedValue));

        if (elementRef.current) {
            // We were previously only calling this if `editing`
            // was enabled via the <InfoAction />.
            // But, since we need to parse the `value` for emojis,
            // we have restructured the components and am now
            // always selecting the full text upon `:focus`.
            // Sadly, solving for this problem makes it so that the
            // editable text cursor does not naturally land at the
            // current mouse position.
            selectAllText(elementRef.current);
        }
    };

    const handleBlur: EditableTextProps['onBlur'] = (editedValue) => {
        setSuppressPlaceholder(Boolean(editedValue));
        onDisableEditing();
    };

    const handleChange: EditableTextProps['onChange'] = (editedValue) => {
        setSuppressPlaceholder(Boolean(editedValue));
        onChange?.(editedValue);
    };

    const placeholderMarkup =
        currentlyEditing && !suppressPlaceholder ? (
            <div className="PlainTextEditor-Placeholder">
                <span className="PlainTextEditor-Placeholder-Text">{trimmedPlaceholder}</span>
            </div>
        ) : null;

    const editableTextMarkup = currentlyEditing ? (
        <EditableText
            editing
            ref={elementRef}
            id={id}
            value={value}
            characterLimit={characterLimit}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            onUpdateValue={onUpdateValue}
        />
    ) : (
        <NonEditableText
            content={value ? value : trimmedPlaceholder}
            heading={heading}
            onAction={editMode ? handleEnableEditing : undefined}
        />
    );

    const textClassNames = classNames('PlainTextEditor-Text', {
        'PlainTextEditor-Text--heading': heading,
        'PlainTextEditor-Text--paragraph': !heading,
    });

    const textMarkup = heading ? (
        <h1 className={textClassNames}>{editableTextMarkup}</h1>
    ) : (
        <p className={textClassNames}>{editableTextMarkup}</p>
    );

    const plainTextEditorClassNames = classNames('PlainTextEditor', {
        'PlainTextEditor--editMode': editMode,
        'PlainTextEditor--editing': currentlyEditing,
        'PlainTextEditor--heading': heading,
        'PlainTextEditor--paragraph': !heading,
    });

    return (
        <div className={plainTextEditorClassNames}>
            <div className="PlainTextEditor-Interior">
                {placeholderMarkup}
                {textMarkup}
            </div>
        </div>
    );
}
